"use strict";

import { getStoredUserInfo } from "../utils/storage";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  FormControl,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CollectionsBookmarkIcon from "@mui/icons-material/CollectionsBookmark";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DriveFolderUploadIcon from "@mui/icons-material/DriveFolderUpload";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import LinkIcon from "@mui/icons-material/Link";
import MovieCreationIcon from "@mui/icons-material/MovieCreation";
import SearchIcon from "@mui/icons-material/Search";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import { ToastContainer, toast } from "react-toastify";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { storage } from "../config/firebase";
import {
  CREATE_NEW_COLLECTION,
  GET_TEAM_COLLECTIONS,
  MEDIA_INGEST,
  UPLOAD_AUDIO_DROPBOX,
  URL_UPLOAD,
} from "../ApiEndpoints";
import { getPublicStorageUrl, getPublicStorageUrlFromGsPath } from "../utils/storageUrls";
import { injectToken } from "../utils/injectToken";
import { fetchManagedTeams } from "../utils/teamRequests";

const CREATE_PROJECTS_COLLECTIONS_STORAGE_KEY = "pendingCreateProjectsCollections";

/*
  Backend integration
  -------------------
  Asset collections and video URL ingestion use the backend proxy endpoints
  documented in Swagger. Audio uploads still use the existing upload helpers.
*/
const normalizeTeams = (rawTeams = []) => {
  const teamsById = new Map();

  rawTeams.forEach((team) => {
    if (typeof team === "string") {
      teamsById.set(team, { id: team, name: team });
      return;
    }

    const id = team?.team_id || team?.teamId || team?.id || "";

    if (!id) {
      return;
    }

    teamsById.set(id, {
      id,
      name: team.team_name || team.teamName || team.name || id,
    });
  });

  return Array.from(teamsById.values());
};

const getStoredUserId = () => {
  try {
    const userInfo = getStoredUserInfo({});
    return userInfo.id || userInfo.userId || userInfo.uid || "";
  } catch (error) {
    console.error("Failed to read current user id:", error);
    return "";
  }
};

const fetchAvailableTeams = async () => {
  const teams = await fetchManagedTeams({ requesterUserId: getStoredUserId() });

  return normalizeTeams(teams);
};

const getTeamLoadErrorMessage = (error) => {
  const serverMessage = error?.response?.data?.message || error?.response?.data?.error;

  if (serverMessage) {
    return serverMessage;
  }

  if (error?.message === "Missing auth token") {
    return "Sign in again to load teams";
  }

  return "Could not load teams";
};

const getBackendHeaders = async () => {
  const headers = await injectToken();

  if (!headers?.Authorization) {
    throw new Error("Missing auth token");
  }

  return headers;
};

const inferAssetType = (file = {}) => {
  const type = file.assetType || file.asset_type || file.mediaType || file.media_type || file.type || "";
  const mimeType = file.mimeType || file.mime_type || "";

  if (String(type).toLowerCase().includes("audio") || mimeType.startsWith("audio/")) {
    return "audio";
  }

  return "video";
};

const normalizeCollectionFile = (file = {}, collectionId = "") => {
  const url =
    file.url ||
    file.downloadUrl ||
    file.download_url ||
    file.sourceUrl ||
    file.source_url ||
    file.gs_src ||
    file.gcsUri ||
    file.gcs_uri ||
    "";
  const fileName =
    file.fileName ||
    file.originalFileName ||
    file.original_file_name ||
    file.displayName ||
    file.display_name ||
    file.name ||
    file.file_name ||
    (url ? getDraftDisplayName({ url }) : "Uploaded file");
  const displayName = file.displayName || file.display_name || fileName;
  const mediaType = file.mediaType || file.media_type || inferAssetType(file);
  const status = file.status && typeof file.status === "object" ? file.status : {};

  return {
    ...file,
    id: file.id || file.assetId || file.asset_id || file.media_id || url || createClientId("file"),
    teamId: file.teamId || file.team_id || "",
    collectionId: file.assetCollectionId || file.asset_collection_id || collectionId,
    assetCollectionId: file.assetCollectionId || file.asset_collection_id || collectionId,
    displayName,
    originalFileName: file.originalFileName || file.original_file_name || fileName,
    assetType: mediaType,
    mediaType,
    fileName,
    size: file.size || file.file_size || 0,
    mimeType: file.mimeType || file.mime_type || "",
    uploadSource: file.uploadSource || file.upload_source || file.source || "",
    assetRole: file.assetRole || file.asset_role || "",
    versionClass: file.versionClass || file.version_class || "",
    versionGroupId: file.versionGroupId || file.version_group_id || "",
    languageCode: file.languageCode || file.language_code || "",
    languageCodes: file.languageCodes || file.language_codes || [],
    status,
    createdBy: file.createdBy || file.created_by || "",
    url: url?.replace?.("gs://", "https://storage.googleapis.com/") || url,
    uploadedAt: file.uploadedAt || file.uploaded_at || file.createdAt || file.created_at || "",
    createdAt: file.createdAt || file.created_at || "",
    updatedAt: file.updatedAt || file.updated_at || "",
    isDeleted: Boolean(file.isDeleted || file.is_deleted),
  };
};

const normalizeCollection = ({ collection, fallbackName = "Untitled Collection", fallbackDescription = "" }) => {
  const id =
    collection.id ||
    collection.asset_collection_id ||
    collection.assetCollectionId ||
    collection.collection_id ||
    collection.collectionId ||
    createClientId("collection");
  const teamId = collection.teamId || collection.team_id || "";
  const displayName =
    collection.displayName ||
    collection.display_name ||
    collection.name ||
    collection.collection_name ||
    fallbackName;
  const createdBy = collection.createdBy || collection.created_by || "";
  const assets = collection.assets || collection.files || collection.media || [];
  const normalizedAssets = Array.isArray(assets)
    ? assets.map((file) => normalizeCollectionFile(file, id))
    : [];

  return {
    ...collection,
    id,
    teamId,
    displayName,
    name: displayName,
    description: collection.description ?? fallbackDescription,
    createdBy,
    assets: normalizedAssets,
    files: normalizedAssets,
    createdAt: collection.createdAt || collection.created_at || new Date().toISOString(),
    updatedAt: collection.updatedAt || collection.updated_at || new Date().toISOString(),
    raw: collection,
  };
};

const parseCreateCollectionResponse = ({ response, fallbackName, fallbackDescription }) => {
  const data = response?.data || {};

  if (data.status === false) {
    throw new Error(data.message || "Collection was not created");
  }

  const collectionId = data.collectionId || data.collection_id || data.data?.collectionId || data.data?.collection_id;
  const collection =
    data.collection ||
    data.assetCollection ||
    data.asset_collection ||
    data.data?.collection ||
    data.data?.assetCollection ||
    data.data?.asset_collection ||
    data.data ||
    data;
  const collectionWithResponseId = collectionId
    ? {
        ...collection,
        id: collection.id || collectionId,
      }
    : collection;

  return {
    collection: normalizeCollection({
      collection: collectionWithResponseId,
      fallbackName,
      fallbackDescription,
    }),
    message: data.message || data.data?.message || "Collection created",
  };
};

const createCollectionOnBackend = async ({ name, description, teamId }) => {
  const response = await axios.post(
    CREATE_NEW_COLLECTION,
    {
      teamId,
      displayName: name,
      description,
    },
    {
      headers: await getBackendHeaders(),
    }
  );

  return parseCreateCollectionResponse({
    response,
    fallbackName: name,
    fallbackDescription: description,
  });
};

const fetchTeamCollections = async ({ teamId }) => {
  const response = await axios.post(
    GET_TEAM_COLLECTIONS,
    { teamId },
    {
      headers: await getBackendHeaders(),
    }
  );
  const data = response.data || {};

  if (data.status === false) {
    throw new Error(data.message || "Team collections were not returned");
  }

  const collections = Array.isArray(data.collections)
    ? data.collections
    : [];

  return collections.map((collection) =>
    normalizeCollection({ collection })
  );
};


const extractVideoUrlFromIngestionResponse = (response) => {
  const data = response?.data || {};
  const downloadPath =
    data?.data?.output?.download_path ||
    data?.output?.download_path ||
    data?.blob_path ||
    data?.download_path ||
    "";

  if (downloadPath) {
    return getPublicStorageUrl(downloadPath);
  }

  return (
    data?.data?.output?.url ||
    data?.data?.output?.download_url ||
    data?.videoUrl ||
    data?.url ||
    getPublicStorageUrlFromGsPath(data?.gs_src) ||
    ""
  );
};

const uploadFileToFirebaseStorage = ({ file, storagePath, onProgress }) =>
  new Promise((resolve, reject) => {
    const storageRef = ref(storage, storagePath);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        onProgress?.(progress);
      },
      (error) => {
        reject(error);
      },
      async () => {
        try {
          const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
          resolve(downloadUrl);
        } catch (error) {
          reject(error);
        }
      }
    );
  });

const getAudioTypeCode = (audioType) => {
  const typeMap = {
    dialogue: "DX",
    music: "MU",
    effect: "FX",
    musicEffects: "ME",
    dialogueMusicEffect: "PM",
  };

  return typeMap[audioType] || "OT";
};

const getAudioChannelCode = (channelType) => {
  const channelMap = {
    right: "R",
    left: "L",
    "multi-channel": "M",
  };

  return channelMap[channelType] || "U";
};

const recordAudioMetadata = async ({ collectionItem, draft, url }) => {
  console.log("Added audio metadata for collection");
  // await axios.post(ADD_FILE_METADATA, {
  //   projectId: collectionItem.id,
  //   asset: {
  //     assetType: "audio",
  //     channelType: draft.channelType,
  //     audioType: draft.audioType,
  //     uploadType: draft.audioUploadType,
  //     url,
  //   },
  // });
};

const uploadVideoAsset = async ({ collectionItem, draft, teamId, onProgress }) => {
  let sourceUrl = draft.url;
  let response;

  if (draft.videoSourceType === "file") {
    const videoFile = draft.file;
    const storagePath = `${collectionItem.name}/${videoFile.name}`;

    sourceUrl = await uploadFileToFirebaseStorage({
      file: videoFile,
      storagePath,
      onProgress: (progress) => onProgress?.(Math.min(progress * 0.65, 65)),
    });
  }

  if (draft.videoSourceType === "direct" || draft.videoSourceType === "google-drive") {
    onProgress?.(78);
    response = await axios.post(
      MEDIA_INGEST,
      {
        asset_collection_id: collectionItem.id,
        display_name: getDraftDisplayName({ ...draft, url: sourceUrl }),
        asset_role: "FP",
        url: sourceUrl,
        team_id: teamId,
      },
      {
        headers: await getBackendHeaders(),
      }
    );
  }

  onProgress?.(92);

  const processedVideoUrl = extractVideoUrlFromIngestionResponse(response) || sourceUrl;

  return {
    id: createClientId("file"),
    collectionId: collectionItem.id,
    assetType: "video",
    fileName: getDraftDisplayName({ ...draft, url: processedVideoUrl || sourceUrl }),
    size: draft.file?.size || 0,
    mimeType: draft.file?.type || "",
    uploadSource: draft.videoSourceType,
    url: processedVideoUrl,
    originalUrl: sourceUrl,
    uploadedAt: new Date().toISOString(),
  };
};

const uploadAudioAsset = async ({ collectionItem, draft, onProgress }) => {
  const projectName = collectionItem.name;
  let audioUrl = draft.url;
  let response;

  if (draft.audioUploadType === "directUpload") {
    const audioFile = draft.file;
    const storagePath = `${projectName}/${projectName}_${draft.audioType}_${draft.channelType}.wav`;

    audioUrl = await uploadFileToFirebaseStorage({
      file: audioFile,
      storagePath,
      onProgress: (progress) => onProgress?.(Math.min(progress * 0.8, 80)),
    });

    onProgress?.(90);
    await recordAudioMetadata({ collectionItem, draft, url: audioUrl });
  } else if (draft.audioUploadType === "dropbox") {
    onProgress?.(35);
    response = await axios.post(UPLOAD_AUDIO_DROPBOX, {
      projectName,
      url: draft.url,
      type: `${draft.audioType}_${draft.channelType}`,
    });

    audioUrl = getPublicStorageUrlFromGsPath(response?.data?.gs_src) || response?.data?.url || draft.url;

    onProgress?.(85);
    await recordAudioMetadata({ collectionItem, draft, url: audioUrl });
  } else {
    onProgress?.(35);
    response = await axios.post(URL_UPLOAD, {
      project_id: collectionItem.id,
      url: draft.url,
      asset: {
        type: "audio",
        seq: "LP",
        fps: "2398",
        fmt: "LORO",
        elem: getAudioTypeCode(draft.audioType),
        ch: getAudioChannelCode(draft.channelType),
      },
    });

    audioUrl = getPublicStorageUrlFromGsPath(response?.data?.gs_src) || response?.data?.url || draft.url;

    onProgress?.(85);
    await recordAudioMetadata({ collectionItem, draft, url: audioUrl });
  }

  return {
    id: createClientId("file"),
    collectionId: collectionItem.id,
    assetType: "audio",
    fileName: getDraftDisplayName({ ...draft, url: audioUrl }),
    size: draft.file?.size || 0,
    mimeType: draft.file?.type || "",
    uploadSource: draft.audioUploadType,
    url: audioUrl,
    channelType: draft.channelType,
    audioType: draft.audioType,
    uploadedAt: new Date().toISOString(),
  };
};

const uploadCollectionAssetOnBackend = async ({ collectionItem, draft, teamId, onProgress }) => {
  if (draft.assetType === "video") {
    return uploadVideoAsset({ collectionItem, draft, teamId, onProgress });
  }

  return uploadAudioAsset({ collectionItem, draft, onProgress });
};

const deleteCollectionAssetOnBackend = async () => true;

const createClientId = (prefix) => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return `${prefix}-${crypto.randomUUID()}`;
  }

  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};


const videoSourceOptions = [
  {
    value: "file",
    title: "Upload File",
    description: "Choose a video file from this device.",
    icon: UploadFileIcon,
    placeholder: "Choose video file",
  },
  {
    value: "dropbox",
    title: "Dropbox URL",
    description: "Paste a shared Dropbox video link.",
    icon: LinkIcon,
    placeholder: "Paste Dropbox URL",
  },
  {
    value: "google-drive",
    title: "Google Drive",
    description: "Paste a Google Drive video link.",
    icon: DriveFolderUploadIcon,
    placeholder: "Paste Google Drive URL",
  },
  {
    value: "direct",
    title: "Direct URL",
    description: "Paste a direct mp4/mov video URL.",
    icon: LinkIcon,
    placeholder: "Paste direct video URL",
  },
];

const audioUploadOptions = [
  {
    value: "directUpload",
    title: "Direct Upload",
    description: "Choose an audio file from this device.",
    icon: UploadFileIcon,
    placeholder: "Choose audio file",
  },
  {
    value: "googleDrive",
    title: "Google Drive",
    description: "Paste a Google Drive audio link.",
    icon: DriveFolderUploadIcon,
    placeholder: "Paste Google Drive URL",
  },
  {
    value: "dropbox",
    title: "Dropbox",
    description: "Paste a shared Dropbox audio link.",
    icon: LinkIcon,
    placeholder: "Paste Dropbox URL",
  },
  {
    value: "sharePoint",
    title: "SharePoint",
    description: "Paste a SharePoint audio link.",
    icon: LinkIcon,
    placeholder: "Paste SharePoint URL",
  },
  {
    value: "azureBlob",
    title: "Azure Blob",
    description: "Paste an Azure Blob audio URL.",
    icon: LinkIcon,
    placeholder: "Paste Azure Blob URL",
  },
];

const channelOptions = [
  { value: "right", label: "Right" },
  { value: "left", label: "Left" },
  { value: "multi-channel", label: "Multi-channel" },
];

const audioTypeOptions = [
  { value: "dialogue", label: "Dialogue" },
  { value: "music", label: "Music" },
  { value: "effect", label: "Effect" },
  { value: "musicEffects", label: "Music & Effects" },
  { value: "dialogueMusicEffect", label: "Dialogue, Music, Effects" },
];

const pageSx = {
  minHeight: "calc(100vh - 90px)",
  width: "100%",
  maxWidth: "100vw",
  py: { xs: 4, md: 6 },
  px: { xs: 1.5, sm: 2, md: 3 },
  boxSizing: "border-box",
  fontFamily: "var(--font-body)",
  background:
    "radial-gradient(circle at 18% 12%, rgba(0, 149, 199, 0.18), transparent 28%), radial-gradient(circle at 86% 24%, rgba(47, 188, 179, 0.16), transparent 30%), linear-gradient(135deg, #eaf7fa 0%, #e6f3ef 50%, #f7fbfc 100%)",
  backgroundAttachment: "fixed",
  position: "relative",
  overflowX: "hidden",
  "& *": {
    boxSizing: "border-box",
  },
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    opacity: 0.45,
    backgroundImage:
      "linear-gradient(45deg, rgba(255,255,255,0.35) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.35) 50%, rgba(255,255,255,0.35) 75%, transparent 75%, transparent)",
    backgroundSize: "12px 12px",
  },
};

const shellSx = {
  width: "100%",
  maxWidth: "1360px",
  mx: "auto",
  position: "relative",
  zIndex: 1,
  minWidth: 0,
};

const glassCardSx = {
  borderRadius: { xs: "24px", md: "32px" },
  border: "1px solid rgba(255,255,255,0.72)",
  background: "rgba(255,255,255,0.88)",
  boxShadow: "0 24px 80px rgba(13, 57, 78, 0.14)",
  backdropFilter: "blur(18px)",
  minWidth: 0,
  overflow: "hidden",
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "18px",
    backgroundColor: "rgba(255,255,255,0.86)",
    transition: "all 180ms ease",
    "& fieldset": {
      borderColor: "rgba(8, 72, 96, 0.16)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(0, 120, 160, 0.45)",
    },
    "&.Mui-focused": {
      boxShadow: "0 0 0 4px rgba(0, 124, 166, 0.10)",
    },
  },
};

const selectSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "16px",
    backgroundColor: "rgba(255,255,255,0.86)",
    "& fieldset": { borderColor: "rgba(8, 72, 96, 0.16)" },
    "&:hover fieldset": { borderColor: "rgba(0,119,159,0.45)" },
    "&.Mui-focused": { boxShadow: "0 0 0 4px rgba(0,119,159,0.10)" },
  },
};

const primaryButtonSx = {
  minHeight: 48,
  px: 3,
  borderRadius: "16px",
  fontWeight: 900,
  textTransform: "none",
  background: "linear-gradient(135deg, #00779f 0%, #00a4c7 100%)",
  boxShadow: "0 14px 28px rgba(0, 119, 159, 0.24)",
  "&:hover": {
    background: "linear-gradient(135deg, #00698d 0%, #0094b5 100%)",
    boxShadow: "0 18px 34px rgba(0, 119, 159, 0.28)",
  },
  "&.Mui-disabled": {
    color: "rgba(255,255,255,0.72)",
    background: "#94a3b8",
  },
};

const outlineButtonSx = {
  minHeight: 44,
  borderRadius: "15px",
  textTransform: "none",
  fontWeight: 900,
  borderColor: "rgba(0,119,159,0.28)",
  color: "#00779f",
  "&:hover": {
    borderColor: "#00779f",
    backgroundColor: "rgba(0,119,159,0.08)",
  },
};

const FieldLabel = ({ children }) => (
  <Typography fontWeight={900} mb={1} color="#243447">
    {children}
  </Typography>
);

const SectionCard = ({ icon: Icon, title, eyebrow, children, action }) => (
  <Paper elevation={0} sx={{ ...glassCardSx, p: { xs: 2, md: 3.5 } }}>
    <Stack
      direction={{ xs: "column", sm: "row" }}
      justifyContent="space-between"
      alignItems={{ xs: "flex-start", sm: "center" }}
      spacing={2}
      mb={3}
      minWidth={0}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" minWidth={0}>
        {Icon && (
          <Avatar
            sx={{
              width: 42,
              height: 42,
              color: "#00779f",
              bgcolor: "rgba(0, 119, 159, 0.10)",
              borderRadius: "14px",
              flexShrink: 0,
            }}
          >
            <Icon fontSize="small" />
          </Avatar>
        )}
        <Box minWidth={0}>
          {eyebrow && (
            <Typography color="text.secondary" fontSize={12} fontWeight={900} textTransform="uppercase">
              {eyebrow}
            </Typography>
          )}
          <Typography fontSize={{ xs: 20, md: 24 }} fontWeight={950} color="#0e1d2b">
            {title}
          </Typography>
        </Box>
      </Stack>
      {action}
    </Stack>
    {children}
  </Paper>
);

const StatusChip = ({ done, label }) => (
  <Chip
    icon={done ? <CheckCircleIcon /> : undefined}
    label={label}
    sx={{
      maxWidth: "100%",
      fontWeight: 900,
      color: done ? "#047857" : "#64748b",
      bgcolor: done ? "rgba(16,185,129,0.12)" : "rgba(100,116,139,0.10)",
      "& .MuiChip-icon": { color: done ? "#047857" : "#64748b" },
    }}
  />
);

const OptionCard = ({ active, icon: Icon, title, description, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      width: "100%",
      height: "100%",
      p: 2,
      textAlign: "left",
      borderRadius: "22px",
      border: `1px solid ${active ? "#00779f" : "rgba(7, 43, 59, 0.10)"}`,
      background: active
        ? "linear-gradient(135deg, rgba(0,119,159,0.13), rgba(255,255,255,0.94))"
        : "rgba(255,255,255,0.72)",
      boxShadow: active
        ? "0 18px 42px rgba(0, 119, 159, 0.16)"
        : "0 12px 30px rgba(13, 57, 78, 0.08)",
      cursor: "pointer",
      transition: "all 180ms ease",
      display: "flex",
      gap: 1.5,
      alignItems: "flex-start",
      minWidth: 0,
      "&:hover": {
        transform: "translateY(-2px)",
        borderColor: "#00779f",
        boxShadow: "0 18px 42px rgba(0, 119, 159, 0.14)",
      },
    }}
  >
    <Avatar
      sx={{
        mt: 0.25,
        width: 42,
        height: 42,
        color: active ? "#fff" : "#00779f",
        bgcolor: active ? "#00779f" : "rgba(0, 119, 159, 0.10)",
        borderRadius: "16px",
        flexShrink: 0,
      }}
    >
      <Icon fontSize="small" />
    </Avatar>
    <Box flex={1} minWidth={0}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" minWidth={0}>
        <Typography fontWeight={950} fontSize={16.5} color="#142234" noWrap>
          {title}
        </Typography>
        {active && <CheckCircleIcon sx={{ color: "#00779f", fontSize: 20, flexShrink: 0 }} />}
      </Stack>
      {description && (
        <Typography mt={0.75} fontSize={13.5} lineHeight={1.55} color="#64748b">
          {description}
        </Typography>
      )}
    </Box>
  </Box>
);

const CompactOptionCard = ({ active, icon: Icon, title, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      width: "100%",
      minHeight: 78,
      p: 1.5,
      textAlign: "left",
      borderRadius: "20px",
      border: `1px solid ${active ? "#00779f" : "rgba(7, 43, 59, 0.10)"}`,
      background: active
        ? "linear-gradient(135deg, rgba(0,119,159,0.13), rgba(255,255,255,0.94))"
        : "rgba(255,255,255,0.76)",
      boxShadow: active
        ? "0 16px 34px rgba(0, 119, 159, 0.15)"
        : "0 10px 24px rgba(13, 57, 78, 0.07)",
      cursor: "pointer",
      transition: "all 180ms ease",
      display: "flex",
      gap: 1.25,
      alignItems: "center",
      minWidth: 0,
      overflow: "hidden",
      "&:hover": {
        transform: "translateY(-2px)",
        borderColor: "#00779f",
        boxShadow: "0 16px 34px rgba(0, 119, 159, 0.14)",
      },
    }}
  >
    <Avatar
      sx={{
        width: 38,
        height: 38,
        color: active ? "#fff" : "#00779f",
        bgcolor: active ? "#00779f" : "rgba(0, 119, 159, 0.10)",
        borderRadius: "14px",
        flexShrink: 0,
      }}
    >
      <Icon fontSize="small" />
    </Avatar>

    <Typography
      flex={1}
      minWidth={0}
      fontWeight={950}
      fontSize={15.5}
      lineHeight={1.25}
      color="#142234"
    >
      {title}
    </Typography>

    {active && <CheckCircleIcon sx={{ color: "#00779f", fontSize: 20, flexShrink: 0 }} />}
  </Box>
);


const getEmptyUploadDraft = () => ({
  assetType: "video",
  videoSourceType: "file",
  audioUploadType: "directUpload",
  channelType: "right",
  audioType: "dialogue",
  file: null,
  url: "",
});

const isUrlBasedVideo = (draft) => draft.assetType === "video" && draft.videoSourceType !== "file";
const isUrlBasedAudio = (draft) => draft.assetType === "audio" && draft.audioUploadType !== "directUpload";
const isUrlBasedDraft = (draft) => isUrlBasedVideo(draft) || isUrlBasedAudio(draft);

const isValidUrl = (value = "") => /^(https?|ftp):\/\/[^\s"]+$/i.test(value.trim());

const formatBytes = (bytes = 0) => {
  if (!bytes) return "0 B";
  const sizes = ["B", "KB", "MB", "GB"];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), sizes.length - 1);
  return `${(bytes / 1024 ** index).toFixed(index === 0 ? 0 : 1)} ${sizes[index]}`;
};

const getFileIcon = (assetType) => {
  if (assetType === "video") return MovieCreationIcon;
  if (assetType === "audio") return AudiotrackIcon;
  return InsertDriveFileIcon;
};

const getSourceLabel = (file) => {
  const allOptions = [...videoSourceOptions, ...audioUploadOptions];
  return allOptions.find((option) => option.value === file.uploadSource)?.title || file.uploadSource || "Source";
};

const getAssetStatusLabel = (status = {}) => {
  if (!status || typeof status !== "object") {
    return "";
  }

  return Object.entries(status)
    .filter(([, value]) => value)
    .map(([key, value]) => `${key}: ${value}`)
    .join(", ");
};

const getFileTypeLabel = (file) => {
  const role = file.assetRole ? ` - ${file.assetRole}` : "";
  const language = file.languageCode ? ` - ${file.languageCode}` : "";

  if (file.assetType === "video") return `Video${role}${language}`;
  if (file.assetType === "audio") return `${file.audioType || "audio"} - ${file.channelType || "channel"}${role}${language}`;

  if (file.assetType === "video") return `Video - ${getSourceLabel(file)}`;
  if (file.assetType === "audio") {
    return `${file.audioType || "audio"} - ${file.channelType || "channel"} - ${getSourceLabel(file)}`;
  }
  return "File";
};

const getDraftDisplayName = (draft) => {
  if (draft.file?.name) return draft.file.name;

  if (draft.url) {
    try {
      const parsedUrl = new URL(draft.url);
      const lastSegment = parsedUrl.pathname.split("/").filter(Boolean).pop();
      return decodeURIComponent(lastSegment || parsedUrl.hostname);
    } catch {
      return draft.url;
    }
  }

  if (draft.assetType === "video") {
    return videoSourceOptions.find((option) => option.value === draft.videoSourceType)?.title || "Video source";
  }

  return audioUploadOptions.find((option) => option.value === draft.audioUploadType)?.title || "Audio source";
};

const getDraftSourceOption = (draft) => {
  if (draft.assetType === "video") {
    return videoSourceOptions.find((option) => option.value === draft.videoSourceType) || videoSourceOptions[0];
  }

  return audioUploadOptions.find((option) => option.value === draft.audioUploadType) || audioUploadOptions[0];
};

const FileChipPreview = ({ file }) => {
  const Icon = getFileIcon(file.assetType);

  return (
    <Tooltip title={file.fileName || "Uploaded file"}>
      <Chip
        icon={<Icon />}
        label={file.fileName || "File"}
        sx={{
          maxWidth: "100%",
          fontWeight: 850,
          color: file.assetType === "video" ? "#075e7b" : "#334155",
          bgcolor: file.assetType === "video" ? "rgba(0,119,159,0.10)" : "rgba(15,23,42,0.06)",
          "& .MuiChip-label": {
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
          "& .MuiChip-icon": {
            color: file.assetType === "video" ? "#00779f" : "#64748b",
          },
        }}
      />
    </Tooltip>
  );
};

const EmptyState = ({ title, description }) => (
  <Box
    sx={{
      py: 6,
      px: 2,
      borderRadius: "28px",
      textAlign: "center",
      border: "1px dashed rgba(7,43,59,0.14)",
      bgcolor: "rgba(248,250,252,0.72)",
    }}
  >
    <CollectionsBookmarkIcon sx={{ color: "#94a3b8", fontSize: 44, mb: 1 }} />
    <Typography fontWeight={950} color="#334155" fontSize={18}>
      {title}
    </Typography>
    <Typography color="#64748b" fontSize={14.5} mt={0.5}>
      {description}
    </Typography>
  </Box>
);

const FileUploadScreen = () => {
  const navigate = useNavigate();
  const [availableTeams, setAvailableTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [teamsError, setTeamsError] = useState("");
  const [collectionsList, setCollectionsList] = useState([]);
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [collectionsError, setCollectionsError] = useState("");
  const [creatingCollection, setCreatingCollection] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [newCollectionName, setNewCollectionName] = useState("");
  const [newCollectionDescription, setNewCollectionDescription] = useState("");
  const [searchText, setSearchText] = useState("");
  const [expandedCollectionId, setExpandedCollectionId] = useState(null);
  const [uploadDrafts, setUploadDrafts] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadingByCollection, setUploadingByCollection] = useState({});
  const [selectedCollectionIds, setSelectedCollectionIds] = useState({});

  useEffect(() => {
    let isMounted = true;

    const loadTeams = async () => {
      try {
        setTeamsLoading(true);
        const teams = await fetchAvailableTeams();

        if (!isMounted) {
          return;
        }

        setAvailableTeams(teams);
        setSelectedTeamId((currentTeamId) => {
          if (currentTeamId && teams.some((team) => team.id === currentTeamId)) {
            return currentTeamId;
          }

          return teams.length === 1 ? teams[0].id : "";
        });
        setTeamsError("");
      } catch (error) {
        console.error("Failed to load teams:", error);

        if (!isMounted) {
          return;
        }

        setAvailableTeams([]);
        setSelectedTeamId("");
        setTeamsError(getTeamLoadErrorMessage(error));
      } finally {
        if (isMounted) {
          setTeamsLoading(false);
        }
      }
    };

    loadTeams();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadCollections = async () => {
      if (!selectedTeamId) {
        setCollectionsList([]);
        setCollectionsError("");
        setExpandedCollectionId(null);
        setSelectedCollectionIds({});
        return;
      }

      try {
        setCollectionsLoading(true);
        setCollectionsError("");
        const collections = await fetchTeamCollections({ teamId: selectedTeamId });

        if (!isMounted) {
          return;
        }

        setCollectionsList(collections);
        setSelectedCollectionIds({});
        setUploadProgress({});
        setUploadingByCollection({});
        setUploadDrafts((prev) => {
          const nextDrafts = {};
          collections.forEach((collection) => {
            nextDrafts[collection.id] = prev[collection.id] || getEmptyUploadDraft();
          });
          return nextDrafts;
        });
        setExpandedCollectionId(null);
      } catch (error) {
        console.error("Failed to load team collections:", error);

        if (!isMounted) {
          return;
        }

        setCollectionsList([]);
        setSelectedCollectionIds({});
        setUploadDrafts({});
        setUploadProgress({});
        setUploadingByCollection({});
        setExpandedCollectionId(null);
        setCollectionsError(error?.response?.data?.message || error?.message || "Could not load team collections");
      } finally {
        if (isMounted) {
          setCollectionsLoading(false);
        }
      }
    };

    loadCollections();

    return () => {
      isMounted = false;
    };
  }, [selectedTeamId]);

  const totalFiles = useMemo(
    () => collectionsList.reduce((sum, item) => sum + (item.files?.length || 0), 0),
    [collectionsList]
  );

  const totalVideos = useMemo(
    () =>
      collectionsList.reduce(
        (sum, item) => sum + (item.files || []).filter((file) => file.assetType === "video").length,
        0
      ),
    [collectionsList]
  );

  const totalAudios = useMemo(
    () =>
      collectionsList.reduce(
        (sum, item) => sum + (item.files || []).filter((file) => file.assetType === "audio").length,
        0
      ),
    [collectionsList]
  );

  const filteredCollections = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();

    if (!normalizedSearch) {
      return collectionsList;
    }

    return collectionsList.filter((item) => {
      const files = item.files || [];
      return (
        item.name?.toLowerCase().includes(normalizedSearch) ||
        item.description?.toLowerCase().includes(normalizedSearch) ||
        files.some((file) => file.fileName?.toLowerCase().includes(normalizedSearch))
      );
    });
  }, [collectionsList, searchText]);

  const selectedCollections = useMemo(
    () => collectionsList.filter((collection) => selectedCollectionIds[collection.id]),
    [collectionsList, selectedCollectionIds]
  );

  const selectedCollectionCount = selectedCollections.length;

  const toggleSelectedCollection = (collectionId) => {
    setSelectedCollectionIds((prev) => {
      const next = { ...prev };

      if (next[collectionId]) {
        delete next[collectionId];
      } else {
        next[collectionId] = true;
      }

      return next;
    });
  };

  const goToCreateProjects = () => {
    if (selectedCollections.length === 0) {
      toast.error("Select at least one collection");
      return;
    }

    const createProjectsPayload = {
      teamId: selectedTeamId,
      collections: selectedCollections.map((collection) => ({
        id: collection.id,
        name: collection.name,
        displayName: collection.displayName,
        description: collection.description,
        files: collection.files || [],
      })),
    };

    sessionStorage.setItem(
      CREATE_PROJECTS_COLLECTIONS_STORAGE_KEY,
      JSON.stringify(createProjectsPayload)
    );

    navigate("/createprojects", {
      state: createProjectsPayload,
    });
  };

  const createCollection = async () => {
    const cleanName = newCollectionName.trim();
    const cleanDescription = newCollectionDescription.trim();
    const teamId = selectedTeamId;

    if (!cleanName) {
      toast.error("Collection name is required");
      return;
    }

    if (!cleanDescription) {
      toast.error("Collection description is required");
      return;
    }

    if (!teamId) {
      toast.error("Team id is required to create a collection");
      return;
    }

    setCreatingCollection(true);

    try {
      const {
        collection: createdCollection,
        message: createCollectionMessage,
      } = await createCollectionOnBackend({
        name: cleanName,
        description: cleanDescription,
        teamId,
      });
      const refreshedCollections = await fetchTeamCollections({ teamId });
      const createdCollectionExists = refreshedCollections.some(
        (collection) => collection.id === createdCollection.id
      );
      const nextCollections = createdCollectionExists
        ? refreshedCollections
        : [createdCollection, ...refreshedCollections];

      setCollectionsList(nextCollections);
      setUploadDrafts((prev) => {
        const nextDrafts = {};
        nextCollections.forEach((collection) => {
          nextDrafts[collection.id] = prev[collection.id] || getEmptyUploadDraft();
        });
        return nextDrafts;
      });
      setExpandedCollectionId(createdCollection.id);
      setCollectionsError("");
      setNewCollectionName("");
      setNewCollectionDescription("");
      toast.success(createCollectionMessage);
    } catch (error) {
      console.error("Failed to create collection:", error);
      toast.error(error?.response?.data?.message || error?.message || "Could not create collection");
    } finally {
      setCreatingCollection(false);
    }
  };

  const getDraft = (collectionId) => uploadDrafts[collectionId] || getEmptyUploadDraft();

  const updateUploadDraft = (collectionId, patch) => {
    setUploadDrafts((prev) => ({
      ...prev,
      [collectionId]: {
        ...getEmptyUploadDraft(),
        ...(prev[collectionId] || {}),
        ...patch,
      },
    }));
  };

  const resetDraftSource = (collectionId, patch) => {
    updateUploadDraft(collectionId, {
      file: null,
      url: "",
      ...patch,
    });
    setUploadProgress((prev) => ({ ...prev, [collectionId]: 0 }));
  };

  const toggleCollection = (collectionId) => {
    setExpandedCollectionId((prev) => (prev === collectionId ? null : collectionId));
    setUploadDrafts((prev) => ({
      ...prev,
      [collectionId]: prev[collectionId] || getEmptyUploadDraft(),
    }));
  };

  const canUpload = (collectionId) => {
    const draft = getDraft(collectionId);

    if (uploadingByCollection[collectionId]) {
      return false;
    }

    if (draft.assetType === "audio" && (!draft.channelType || !draft.audioType)) {
      return false;
    }

    if (isUrlBasedDraft(draft)) {
      return isValidUrl(draft.url);
    }

    return Boolean(draft.file);
  };

  const uploadFileToCollection = async (collectionItem) => {
    const draft = getDraft(collectionItem.id);
    const shouldIngestVideo =
      draft.assetType === "video" &&
      (draft.videoSourceType === "direct" || draft.videoSourceType === "google-drive");
    const teamId = shouldIngestVideo ? selectedTeamId : "";

    if (isUrlBasedDraft(draft) && !isValidUrl(draft.url)) {
      toast.error("Enter a valid URL first");
      return;
    }

    if (!isUrlBasedDraft(draft) && !draft.file) {
      toast.error("Choose a file first");
      return;
    }

    if (draft.assetType === "audio" && (!draft.channelType || !draft.audioType)) {
      toast.error("Choose audio channel and type");
      return;
    }

    if (shouldIngestVideo && !teamId) {
      toast.error("Team id is required to ingest video");
      return;
    }

    setUploadingByCollection((prev) => ({ ...prev, [collectionItem.id]: true }));
    setUploadProgress((prev) => ({ ...prev, [collectionItem.id]: 35 }));

    try {
      const fileMetadata = await uploadCollectionAssetOnBackend({
        collectionItem,
        draft,
        teamId,
        onProgress: (progress) => {
          setUploadProgress((prev) => ({
            ...prev,
            [collectionItem.id]: Math.min(Math.floor(progress), 99),
          }));
        },
      });

      setUploadProgress((prev) => ({ ...prev, [collectionItem.id]: 100 }));
      setCollectionsList((prev) =>
        prev.map((item) =>
          item.id === collectionItem.id
            ? {
                ...item,
                files: [...(item.files || []), fileMetadata],
                updatedAt: new Date().toISOString(),
              }
            : item
        )
      );

      updateUploadDraft(collectionItem.id, { file: null, url: "" });
      toast.success("File added to collection");
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error("Could not add file");
    } finally {
      setUploadingByCollection((prev) => ({ ...prev, [collectionItem.id]: false }));
    }
  };

  const removeFileFromCollection = async (collectionItem, file) => {
    try {
      await deleteCollectionAssetOnBackend({ collectionId: collectionItem.id, file });

      setCollectionsList((prev) =>
        prev.map((item) =>
          item.id === collectionItem.id
            ? {
                ...item,
                files: (item.files || []).filter((existingFile) => existingFile.id !== file.id),
                updatedAt: new Date().toISOString(),
              }
            : item
        )
      );

      toast.success("File removed from collection");
    } catch (error) {
      console.error("Failed to remove file:", error);
      toast.error("Could not remove file");
    }
  };

  const renderSourceOptions = (collectionItem, draft) => {
    const options = draft.assetType === "video" ? videoSourceOptions : audioUploadOptions;

    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(auto-fit, minmax(150px, 1fr))",
          },
          gap: 1.5,
          alignItems: "stretch",
          minWidth: 0,
          width: "100%",
        }}
      >
        {options.map((option) => {
          const active = draft.assetType === "video"
            ? draft.videoSourceType === option.value
            : draft.audioUploadType === option.value;

          return (
            <CompactOptionCard
              key={option.value}
              active={active}
              icon={option.icon}
              title={option.title}
              onClick={() => {
                if (draft.assetType === "video") {
                  resetDraftSource(collectionItem.id, { videoSourceType: option.value });
                } else {
                  resetDraftSource(collectionItem.id, { audioUploadType: option.value });
                }
              }}
            />
          );
        })}
      </Box>
    );
  };

  const renderUploadInput = (collectionItem, draft) => {
    const sourceOption = getDraftSourceOption(draft);

    if (!isUrlBasedDraft(draft)) {
      return (
        <Button
          component="label"
          fullWidth
          variant="outlined"
          startIcon={<UploadFileIcon />}
          sx={{
            ...outlineButtonSx,
            minHeight: 66,
            justifyContent: "flex-start",
            px: 2.5,
            borderStyle: "dashed",
            bgcolor: "rgba(255,255,255,0.68)",
          }}
        >
          <Box minWidth={0} textAlign="left">
            <Typography fontWeight={950} color="#142234" noWrap>
              {draft.file?.name || sourceOption.placeholder}
            </Typography>
            <Typography fontSize={13.5} color="#64748b" noWrap>
              {draft.file
                ? `${formatBytes(draft.file.size)} - ${draft.file.type || "Unknown type"}`
                : "The file is only added after you press Upload"}
            </Typography>
          </Box>
          <input
            hidden
            type="file"
            accept={draft.assetType === "video" ? "video/*" : "audio/*"}
            onChange={(event) => updateUploadDraft(collectionItem.id, { file: event.target.files?.[0] || null })}
          />
        </Button>
      );
    }

    return (
      <TextField
        fullWidth
        value={draft.url}
        onChange={(event) => updateUploadDraft(collectionItem.id, { url: event.target.value })}
        placeholder={sourceOption.placeholder}
        sx={inputSx}
        InputProps={{
          startAdornment: <LinkIcon sx={{ color: "#64748b", mr: 1, flexShrink: 0 }} />,
        }}
        helperText={draft.url && !isValidUrl(draft.url) ? "Enter a valid URL starting with http:// or https://" : " "}
        error={Boolean(draft.url) && !isValidUrl(draft.url)}
      />
    );
  };

  const renderUploadPanel = (collectionItem) => {
    const draft = getDraft(collectionItem.id);
    const progress = uploadProgress[collectionItem.id] || 0;
    const uploading = uploadingByCollection[collectionItem.id];

    return (
      <Paper
        elevation={0}
        sx={{
          mt: 2.5,
          p: { xs: 2, md: 2.5 },
          borderRadius: "26px",
          border: "1px solid rgba(0,119,159,0.12)",
          background: "linear-gradient(135deg, rgba(0,119,159,0.07), rgba(255,255,255,0.78))",
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        <Stack spacing={2.5} minWidth={0}>
          <Grid container spacing={1.8} alignItems="stretch">
            <Grid item xs={12} md={6} sx={{ minWidth: 0 }}>
              <OptionCard
                active={draft.assetType === "video"}
                icon={VideoLibraryIcon}
                title="Video"
                description="Upload or link a master video/source reference."
                onClick={() => resetDraftSource(collectionItem.id, { assetType: "video" })}
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ minWidth: 0 }}>
              <OptionCard
                active={draft.assetType === "audio"}
                icon={AudiotrackIcon}
                title="Audio"
                description="Add dialogue, music, effects, or multi-channel audio assets."
                onClick={() => resetDraftSource(collectionItem.id, { assetType: "audio" })}
              />
            </Grid>
          </Grid>

          <Box>
            <FieldLabel>{draft.assetType === "video" ? "Video source" : "Audio upload method"}</FieldLabel>
            {renderSourceOptions(collectionItem, draft)}
          </Box>

          {draft.assetType === "audio" && (
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <FieldLabel>Channel</FieldLabel>
                <FormControl fullWidth sx={selectSx}>
                  <Select
                    value={draft.channelType}
                    onChange={(event) => updateUploadDraft(collectionItem.id, { channelType: event.target.value })}
                  >
                    {channelOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FieldLabel>Audio type</FieldLabel>
                <FormControl fullWidth sx={selectSx}>
                  <Select
                    value={draft.audioType}
                    onChange={(event) => updateUploadDraft(collectionItem.id, { audioType: event.target.value })}
                  >
                    {audioTypeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          )}

          <Grid container spacing={2} alignItems="stretch">
            <Grid item xs={12} md={8} sx={{ minWidth: 0 }}>
              {renderUploadInput(collectionItem, draft)}
            </Grid>

            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<CloudUploadIcon />}
                disabled={!canUpload(collectionItem.id)}
                onClick={() => uploadFileToCollection(collectionItem)}
                sx={{ ...primaryButtonSx, height: "100%", minHeight: 66 }}
              >
                {uploading ? "Uploading..." : isUrlBasedDraft(draft) ? "Add URL" : "Upload File"}
              </Button>
            </Grid>

            {(uploading || progress > 0) && (
              <Grid item xs={12}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }}>
                  <Box flex={1} minWidth={0}>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(Math.floor(progress), 100)}
                      sx={{ height: 10, borderRadius: 99, bgcolor: "rgba(0,119,159,0.12)" }}
                    />
                  </Box>
                  <Typography fontWeight={950} color={progress >= 100 ? "#047857" : "#00779f"} minWidth={58}>
                    {Math.min(Math.floor(progress), 100)}%
                  </Typography>
                </Stack>
              </Grid>
            )}
          </Grid>
        </Stack>
      </Paper>
    );
  };

  const renderFilesTable = (collectionItem) => {
    const files = collectionItem.files || [];

    if (files.length === 0) {
      return (
        <Box
          sx={{
            mt: 2,
            py: 4,
            px: 2,
            borderRadius: "22px",
            textAlign: "center",
            border: "1px dashed rgba(7,43,59,0.14)",
            bgcolor: "rgba(248,250,252,0.72)",
          }}
        >
          <InsertDriveFileIcon sx={{ color: "#94a3b8", fontSize: 36, mb: 1 }} />
          <Typography fontWeight={950} color="#334155">
            This collection is empty
          </Typography>
          <Typography color="#64748b" fontSize={14}>
            Add a video or audio file without leaving this page.
          </Typography>
        </Box>
      );
    }

    return (
      <Stack spacing={1.2} mt={2} minWidth={0}>
        {files.map((file) => {
          const Icon = getFileIcon(file.assetType);
          const statusLabel = getAssetStatusLabel(file.status);

          return (
            <Paper
              key={file.id || file.url}
              elevation={0}
              sx={{
                p: 1.4,
                borderRadius: "18px",
                border: "1px solid rgba(7,43,59,0.08)",
                bgcolor: "rgba(255,255,255,0.78)",
                minWidth: 0,
              }}
            >
              <Stack direction="row" spacing={1.4} alignItems="center" minWidth={0}>
                <Avatar
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: "14px",
                    color: file.assetType === "video" ? "#00779f" : "#64748b",
                    bgcolor: file.assetType === "video" ? "rgba(0,119,159,0.10)" : "rgba(15,23,42,0.06)",
                    flexShrink: 0,
                  }}
                >
                  <Icon fontSize="small" />
                </Avatar>
                <Box flex={1} minWidth={0}>
                  <Typography
                    component={file.url ? "a" : "span"}
                    href={file.url || undefined}
                    target={file.url ? "_blank" : undefined}
                    rel={file.url ? "noopener noreferrer" : undefined}
                    fontWeight={950}
                    color="#142234"
                    sx={{
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      textDecoration: "none",
                    }}
                  >
                    {file.fileName || "Uploaded file"}
                  </Typography>
                  <Typography fontSize={13.5} color="#64748b" noWrap>
                    {getFileTypeLabel(file)} - {formatBytes(file.size)}
                  </Typography>
                  {statusLabel && (
                    <Typography fontSize={12.5} color="#64748b" noWrap>
                      {statusLabel}
                    </Typography>
                  )}
                </Box>
                <Tooltip title="Remove from collection">
                  <IconButton
                    onClick={() => removeFileFromCollection(collectionItem, file)}
                    sx={{
                      flexShrink: 0,
                      color: "#ef4444",
                      bgcolor: "rgba(239,68,68,0.08)",
                      "&:hover": { bgcolor: "rgba(239,68,68,0.14)" },
                    }}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Paper>
          );
        })}
      </Stack>
    );
  };

  return (
    <Box sx={pageSx}>
      <ToastContainer position="top-center" />

      <Box sx={shellSx}>
        <Stack alignItems="center" textAlign="center" spacing={1.5} mb={4} minWidth={0}>
          <Avatar
            sx={{
              width: 58,
              height: 58,
              borderRadius: "18px",
              color: "#00779f",
              bgcolor: "rgba(255,255,255,0.88)",
              boxShadow: "0 16px 45px rgba(0, 119, 159, 0.16)",
            }}
          >
            <AutoAwesomeIcon />
          </Avatar>
          <Chip
            label="Media library - Collections"
            sx={{
              fontWeight: 900,
              color: "#00779f",
              bgcolor: "rgba(255,255,255,0.78)",
              border: "1px solid rgba(0,119,159,0.12)",
            }}
          />
          <Typography fontSize={{ xs: 34, md: 46 }} fontWeight={950} letterSpacing={-1.5} color="#0d1724">
            File Uploads
          </Typography>
          <Typography maxWidth={720} color="#5f7188" fontSize={{ xs: 15, md: 17 }}>
            Create collections, upload video and audio files, and preview everything from one clean workspace.
          </Typography>
        </Stack>

        <Stack spacing={3.5} minWidth={0}>
          <Paper elevation={0} sx={{ ...glassCardSx, p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2.5} alignItems="center">
              <Grid item xs={12} md={7} sx={{ minWidth: 0 }}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} flexWrap="wrap" useFlexGap>
                  <StatusChip done label={`${collectionsList.length} collection${collectionsList.length === 1 ? "" : "s"}`} />
                  <StatusChip done={totalFiles > 0} label={`${totalFiles} file${totalFiles === 1 ? "" : "s"}`} />
                  <Chip icon={<MovieCreationIcon />} label={`${totalVideos} videos`} sx={{ fontWeight: 850 }} />
                  <Chip icon={<AudiotrackIcon />} label={`${totalAudios} audios`} sx={{ fontWeight: 850 }} />
                </Stack>
              </Grid>
              <Grid item xs={12} md={5} sx={{ minWidth: 0 }}>
                <TextField
                  fullWidth
                  value={searchText}
                  onChange={(event) => setSearchText(event.target.value)}
                  placeholder="Search collections or files"
                  sx={inputSx}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ color: "#64748b", mr: 1, flexShrink: 0 }} />,
                  }}
                />
              </Grid>
            </Grid>
          </Paper>

          <SectionCard icon={CollectionsBookmarkIcon} title="Team" eyebrow="Workspace">
            <Grid container spacing={2.2} alignItems="center">
              <Grid item xs={12} md={7} sx={{ minWidth: 0 }}>
                <FieldLabel>Available teams</FieldLabel>
                <FormControl fullWidth sx={selectSx}>
                  <Select
                    value={selectedTeamId}
                    displayEmpty
                    disabled={teamsLoading || availableTeams.length === 0}
                    onChange={(event) => setSelectedTeamId(event.target.value)}
                  >
                    <MenuItem value="" disabled>
                      {teamsLoading
                        ? "Loading teams..."
                        : teamsError || (availableTeams.length === 0 ? "No teams available" : "Select a team")}
                    </MenuItem>
                    {availableTeams.map((team) => (
                      <MenuItem key={team.id} value={team.id}>
                        {team.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={5} sx={{ minWidth: 0 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    mt: { xs: 0, md: 3.7 },
                    borderRadius: "18px",
                    border: "1px solid rgba(7,43,59,0.08)",
                    bgcolor: "rgba(248,250,252,0.72)",
                  }}
                >
                  <Typography fontWeight={950} color="#142234" noWrap>
                    {teamsLoading ? "Loading teams" : selectedTeamId ? "Selected team" : "No team selected"}
                  </Typography>
                  <Typography color="#64748b" fontSize={13.5} noWrap>
                    {teamsLoading
                      ? "Fetching teams for your account"
                      : teamsError || selectedTeamId || "Choose a team before creating a collection"}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </SectionCard>

          <SectionCard icon={AddIcon} title="Create Collection" eyebrow="Start here">
            <Grid container spacing={2.2} alignItems="center">
              <Grid item xs={12} md={4} sx={{ minWidth: 0 }}>
                <FieldLabel>Collection name</FieldLabel>
                <TextField
                  fullWidth
                  value={newCollectionName}
                  onChange={(event) => setNewCollectionName(event.target.value)}
                  placeholder="Example: Project sources"
                  sx={inputSx}
                />
              </Grid>
              <Grid item xs={12} md={5} sx={{ minWidth: 0 }}>
                <FieldLabel>Description required</FieldLabel>
                <TextField
                  fullWidth
                  value={newCollectionDescription}
                  onChange={(event) => setNewCollectionDescription(event.target.value)}
                  placeholder="Example: Footage selects"
                  sx={inputSx}
                />
              </Grid>
              <Grid item xs={12} md={3} sx={{ minWidth: 0 }}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<AddIcon />}
                  disabled={creatingCollection || collectionsLoading}
                  onClick={createCollection}
                  sx={{ ...primaryButtonSx, mt: { xs: 0, md: 3.7 } }}
                >
                  {creatingCollection ? "Creating..." : "Create Collection"}
                </Button>
              </Grid>
            </Grid>
          </SectionCard>

          <SectionCard
            icon={CollectionsBookmarkIcon}
            title="Collections"
            eyebrow="Main view"
            action={
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
                {selectedCollectionCount > 0 && (
                  <Button
                    variant="contained"
                    startIcon={<MovieCreationIcon />}
                    onClick={goToCreateProjects}
                    sx={primaryButtonSx}
                  >
                    Create Projects
                  </Button>
                )}
                <Chip
                  label={
                    selectedCollectionCount > 0
                      ? `${selectedCollectionCount} selected`
                      : collectionsLoading ? "Loading" : `${filteredCollections.length} visible`
                  }
                  sx={{ fontWeight: 900, color: "#00779f", bgcolor: "rgba(0,119,159,0.10)" }}
                />
              </Stack>
            }
          >
            {collectionsLoading ? (
              <EmptyState
                title="Loading collections"
                description="Fetching collections for the selected team."
              />
            ) : collectionsError ? (
              <EmptyState
                title="Could not load collections"
                description={collectionsError}
              />
            ) : !selectedTeamId ? (
              <EmptyState
                title="Select a team"
                description="Choose a team to load its collections."
              />
            ) : filteredCollections.length === 0 ? (
              <EmptyState
                title={collectionsList.length === 0 ? "No collections yet" : "No matching collections"}
                description={collectionsList.length === 0 ? "Create your first collection, then upload video and audio files into it." : "Try another search term or create a new collection."}
              />
            ) : (
              <Grid container spacing={2.5} alignItems="stretch">
                {filteredCollections.map((collectionItem) => {
                  const files = collectionItem.files || [];
                  const previewFiles = files.slice(0, 4);
                  const videoCount = files.filter((file) => file.assetType === "video").length;
                  const audioCount = files.filter((file) => file.assetType === "audio").length;
                  const isExpanded = expandedCollectionId === collectionItem.id;
                  const isSelected = Boolean(selectedCollectionIds[collectionItem.id]);

                  return (
                    <Grid item xs={12} lg={6} key={collectionItem.id} sx={{ minWidth: 0 }}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: { xs: 2, md: 2.6 },
                          height: "100%",
                          borderRadius: "28px",
                          border: `1px solid ${isSelected || isExpanded ? "rgba(0,119,159,0.34)" : "rgba(7,43,59,0.08)"}`,
                          bgcolor: isSelected || isExpanded ? "rgba(255,255,255,0.94)" : "rgba(255,255,255,0.76)",
                          boxShadow: isSelected || isExpanded ? "0 20px 50px rgba(0,119,159,0.12)" : "0 14px 34px rgba(13,57,78,0.08)",
                          transition: "all 180ms ease",
                          overflow: "hidden",
                          minWidth: 0,
                        }}
                      >
                        <Stack direction="row" spacing={1.6} alignItems="flex-start" minWidth={0}>
                          <Tooltip title={isSelected ? "Remove from Create Projects" : "Select for Create Projects"}>
                            <Checkbox
                              checked={isSelected}
                              onChange={() => toggleSelectedCollection(collectionItem.id)}
                              sx={{
                                mt: 0.4,
                                p: 0.5,
                                color: "#00779f",
                                "&.Mui-checked": { color: "#00779f" },
                              }}
                            />
                          </Tooltip>
                          <Badge
                            badgeContent={files.length}
                            color="primary"
                            overlap="circular"
                            sx={{
                              flexShrink: 0,
                              "& .MuiBadge-badge": {
                                bgcolor: "#00779f",
                                fontWeight: 900,
                              },
                            }}
                          >
                            <Avatar
                              sx={{
                                width: 52,
                                height: 52,
                                borderRadius: "18px",
                                color: "#00779f",
                                bgcolor: "rgba(0,119,159,0.10)",
                              }}
                            >
                              <FolderOpenIcon />
                            </Avatar>
                          </Badge>

                          <Box flex={1} minWidth={0}>
                            <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start" minWidth={0}>
                              <Box minWidth={0}>
                                <Typography fontWeight={950} color="#142234" fontSize={20} noWrap>
                                  {collectionItem.name}
                                </Typography>
                                <Typography color="#64748b" fontSize={13.5} noWrap>
                                  {collectionItem.description || "No description"}
                                </Typography>
                              </Box>

                              <Tooltip title={isExpanded ? "Collapse" : "View and add files"}>
                                <IconButton
                                  onClick={() => toggleCollection(collectionItem.id)}
                                  sx={{
                                    flexShrink: 0,
                                    color: "#00779f",
                                    bgcolor: "rgba(0,119,159,0.08)",
                                    "&:hover": { bgcolor: "rgba(0,119,159,0.14)" },
                                  }}
                                >
                                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                              </Tooltip>
                            </Stack>

                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mt={1.5}>
                              <Chip icon={<MovieCreationIcon />} label={`${videoCount} video${videoCount === 1 ? "" : "s"}`} sx={{ fontWeight: 850, bgcolor: "rgba(0,119,159,0.08)" }} />
                              <Chip icon={<AudiotrackIcon />} label={`${audioCount} audio${audioCount === 1 ? "" : "s"}`} sx={{ fontWeight: 850, bgcolor: "rgba(15,23,42,0.06)" }} />
                            </Stack>
                          </Box>
                        </Stack>

                        <Divider sx={{ my: 2, borderColor: "rgba(7,43,59,0.08)" }} />

                        {files.length > 0 ? (
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap minWidth={0}>
                            {previewFiles.map((file) => (
                              <Box key={file.id || file.url} sx={{ maxWidth: { xs: "100%", sm: "48%" }, minWidth: 0 }}>
                                <FileChipPreview file={file} />
                              </Box>
                            ))}
                            {files.length > previewFiles.length && (
                              <Chip
                                label={`+${files.length - previewFiles.length} more`}
                                sx={{ fontWeight: 900, color: "#00779f", bgcolor: "rgba(0,119,159,0.10)" }}
                              />
                            )}
                          </Stack>
                        ) : (
                          <Stack direction="row" spacing={1.2} alignItems="center" sx={{ color: "#64748b" }}>
                            <InsertDriveFileIcon />
                            <Typography fontWeight={850} fontSize={14.5}>
                              No files uploaded yet
                            </Typography>
                          </Stack>
                        )}

                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.4} mt={2.4}>
                          <Button
                            variant={isExpanded ? "contained" : "outlined"}
                            startIcon={isExpanded ? <CloseIcon /> : <CloudUploadIcon />}
                            onClick={() => toggleCollection(collectionItem.id)}
                            sx={isExpanded ? primaryButtonSx : outlineButtonSx}
                          >
                            {isExpanded ? "Close panel" : "View / Add files"}
                          </Button>
                        </Stack>

                        <Collapse in={isExpanded} unmountOnExit>
                          {renderUploadPanel(collectionItem)}
                          {renderFilesTable(collectionItem)}
                        </Collapse>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </SectionCard>
        </Stack>
      </Box>
    </Box>
  );
};

export default FileUploadScreen;

